/** The unified pull-plan workspace shell: the Takt BOARD on top and the
 *  predecessor GANTT on the bottom, in ONE view, with a drag splitter between
 *  them and a Board / Gantt / Both pane toggle.
 *
 *  This is the layout shell ONLY — the two panes are injected as slots
 *  (`renderBoard` / `renderGantt`), so the real `WbsBoardView` and
 *  `SequenceView` keep their existing wiring in the host. The shell owns the
 *  pane mode, the board-heavy split height, and the bottom pane's focus header
 *  (which level — or the train — the gantt is showing). All splitter geometry
 *  lives in the pure `splitter.ts` helpers (unit-tested).
 */
import React from "react";
import { clampBoardHeight, defaultBoardHeight } from "./splitter";
import "./UnifiedBoard.css";

export type PaneMode = "board" | "gantt" | "both";

// Must match .unified-board-splitter height in UnifiedBoard.css.
const HANDLE_PX = 9;
// The board can be dragged fully shut so the logic editor takes the whole view
// (it reads as an overlay over the board); the splitter stays put to drag back.
const MIN_BOARD_PX = 0;
// Fallback collapsed-gantt height, used only until the real toolbar-header
// height is measured (see `headerPx`). The collapsed pane shows just that header
// and clips the body + Relationships panel below it.
const MIN_GANTT_PX = 60;
// Drag the grip this far ABOVE board = 0 to go full-page: the host hides the
// train band so the logic editor covers the whole workspace below the header.
const COVER_MARGIN_PX = 44;

const PANE_OPTS: Array<{ key: PaneMode; label: string; title: string }> = [
  { key: "gantt", label: "Logic", title: "Show only the dependency / logic editor" },
  { key: "board", label: "Board", title: "Show only the Takt board" },
  { key: "both", label: "Both", title: "Board on top, logic on the bottom — drag the divider to resize" },
];

/** The Board / Gantt / Both segmented control. Rendered by the host in its top
 *  action bar (not inside the workspace) so it never costs a dedicated row. */
export const PaneModeToggle: React.FC<{
  paneMode: PaneMode;
  onChange: (m: PaneMode) => void;
}> = ({ paneMode, onChange }) => (
  <div className="unified-board-panetoggle" role="group" aria-label="Pane layout">
    {PANE_OPTS.map((o) => (
      <button
        key={o.key}
        type="button"
        title={o.title}
        className={
          "unified-board-panetoggle-btn" +
          (paneMode === o.key ? " unified-board-panetoggle-btn--active" : "")
        }
        onClick={() => onChange(o.key)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

interface Props {
  paneMode: PaneMode;
  /** The top board pane (WbsBoardView + its sidebar / bulk bar). */
  boardSlot: React.ReactNode;
  /** The bottom gantt pane (SequenceView — DepTable + DepGanttStrip). The gantt
   *  renders its own single header (title / focus chip + Board-dates / Forward-
   *  pass / External-ties), so the shell adds no header strip of its own. */
  ganttSlot: React.ReactNode;
  /** Full-page logic: the grip was dragged up past the board to cover the train
   *  band too. The host hides the band; here it just pins the board shut. */
  covered?: boolean;
  /** Fired when the grip crosses the cover threshold (up = true, back down =
   *  false). The host toggles the train band's visibility off it. */
  onCoverChange?: (covered: boolean) => void;
  /** Full-screen: render the gantt pane fixed over the entire viewport. */
  fullscreen?: boolean;
}

const UnifiedBoard: React.FC<Props> = ({
  paneMode,
  boardSlot,
  ganttSlot,
  covered = false,
  onCoverChange,
  fullscreen = false,
}) => {
  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  // Board pane height in px while in "both" mode. null until first measured, at
  // which point it opens with the gantt collapsed to its header.
  const [boardH, setBoardH] = React.useState<number | null>(null);
  const dragRef = React.useRef<{ startY: number; startH: number } | null>(null);

  // The collapsed gantt height = the logic editor's toolbar header, measured live
  // so collapsing lands exactly on the header's bottom line (the toolbar's height
  // changes with its sub-line). Falls back to the static minimum until measured.
  const [headerPx, setHeaderPx] = React.useState(MIN_GANTT_PX);
  React.useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => {
      const bar = el.querySelector(".sequence-view-modes");
      const h = bar ? (bar as HTMLElement).getBoundingClientRect().height : 0;
      if (h > 0) setHeaderPx(Math.round(h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    const bar = el.querySelector(".sequence-view-modes");
    if (bar) ro.observe(bar);
    return () => ro.disconnect();
  }, [paneMode]);

  const clampOpts = React.useCallback(
    (containerPx: number) => ({
      containerPx,
      handlePx: HANDLE_PX,
      minBoardPx: MIN_BOARD_PX,
      minGanttPx: headerPx,
    }),
    [headerPx],
  );

  // Open with the gantt collapsed to its header, and re-clamp on container resize
  // (or when the measured header height changes) so a pane never strands below
  // its minimum.
  React.useLayoutEffect(() => {
    if (paneMode !== "both") return;
    const el = bodyRef.current;
    if (!el) return;
    const apply = () => {
      const h = el.getBoundingClientRect().height;
      if (h <= 0) return;
      setBoardH((prev) =>
        prev == null
          ? defaultBoardHeight(clampOpts(h), headerPx)
          : clampBoardHeight(prev, clampOpts(h)),
      );
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [paneMode, clampOpts, headerPx]);

  const coveredRef = React.useRef(covered);
  coveredRef.current = covered;
  const onHandleDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = bodyRef.current;
    if (!el || boardH == null) return;
    dragRef.current = { startY: e.clientY, startH: boardH };
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      const node = bodyRef.current;
      if (!d || !node) return;
      const containerPx = node.getBoundingClientRect().height;
      // `desired` is the unclamped board height the pointer asks for. Below 0
      // it means "keep dragging up past the shut board" — once it passes the
      // cover margin, the logic goes full-page over the train band.
      const desired = d.startH + (ev.clientY - d.startY);
      setBoardH(clampBoardHeight(desired, clampOpts(containerPx)));
      if (onCoverChange) {
        if (!coveredRef.current && desired < -COVER_MARGIN_PX) onCoverChange(true);
        else if (coveredRef.current && desired > COVER_MARGIN_PX) onCoverChange(false);
      }
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const showBoard = paneMode !== "gantt";
  const showGantt = paneMode !== "board";
  const showSplit = paneMode === "both";

  return (
    <div className="unified-board">
      <div className="unified-board-body" ref={bodyRef}>
        {showBoard && (
          <div
            className="unified-board-pane unified-board-pane--board"
            style={
              showSplit && boardH != null
                ? { height: covered ? 0 : boardH, flex: "none" }
                : undefined
            }
          >
            {boardSlot}
          </div>
        )}

        {showSplit && (
          <div
            className={
              "unified-board-splitter" + (covered ? " unified-board-splitter--covered" : "")
            }
            role="separator"
            aria-orientation="horizontal"
            title="Drag up to grow the logic editor over the board — keep going to go full-page. Drag down to collapse it to its header."
            onMouseDown={onHandleDown}
          >
            <span className="unified-board-splitter-grip" />
          </div>
        )}

        {showGantt && (
          <div
            className={
              "unified-board-pane unified-board-pane--gantt" +
              (fullscreen ? " unified-board-pane--fullscreen" : "")
            }
          >
            <div className="unified-board-gantt-body">{ganttSlot}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedBoard;
